import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  getFinancialPeriods,
  deleteFinancialPeriod,
  closeFinancialPeriod,
  clearError,
  clearSuccess,
} from '../../../../redux/Slices/authSlice';
import {
  FaPlus, FaEye, FaEdit, FaTrash, FaLock, FaPrint, FaFileExcel,
  FaSearch, FaFilter, FaSort, FaSortUp, FaSortDown,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useTitle } from '../../../../shared/hooks/TitleContext';
import * as XLSX from 'xlsx';
import {
  generatePdfFromElementIds,
  generateSingleElementPdf,
  waitForRender,
  datedFileName,
} from '../../../../shared/pdf/pdfEngine';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n/i18n';
import PdfPreviewModal from '../Reports/components/PdfPreviewModal';
import { calculatePdfPagination, convertArabicNumbersToEnglish } from '../../../../shared/utils.js/utils';

// ─── Status options for filter ───────────────────────────────────────────────
const STATUS_OPTIONS = ['open', 'closed', 'soft closed', 'pending'];

// ─── Sortable column field keys ──────────────────────────────────────────────
const SORT_FIELDS = {
  index: 'index',
  name: 'name',
  start_date: 'start_date',
  end_date: 'end_date',
  status: 'status',
  created_by: 'created_by',
  created_at: 'created_at',
};

const FinancialPeriods = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setTitle } = useTitle();
  const { financialPeriodsPagination, isLoading, error, success } = useSelector((state) => state.auth);

  // ─── Core state ────────────────────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [perPageInput, setPerPageInput] = useState(10);
  const [allFinancialPeriods, setAllFinancialPeriods] = useState([]);

  // ─── Search / Filter / Sort state ──────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showFilters, setShowFilters] = useState(false);

  // ─── Modal / PDF state ─────────────────────────────────────────────────────
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [periodToDelete, setPeriodToDelete] = useState(null);
  const [periodToClose, setPeriodToClose] = useState(null);
  const [periodForPrint, setPeriodForPrint] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfType, setPdfType] = useState(null); // 'single' | 'all'
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [pdfChunks, setPdfChunks] = useState([]);

  const lastErrorRef = useRef({ message: null, time: 0 });
  const lastSuccessRef = useRef({ message: null, time: 0 });

  const { t } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';
  const publicUrl = process.env.PUBLIC_URL || '';
  const headerLogo = `${publicUrl}/ACE.png`;

  // ─── Page title ────────────────────────────────────────────────────────────
  useEffect(() => {
    setTitle('sidenav.financialPeriods');
  }, [setTitle]);

  // ─── Debounce search (400 ms) ──────────────────────────────────────────────
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  // ─── Reset page when filters/sort change ──────────────────────────────────
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, dateFrom, dateTo, sortField, sortOrder]);

  // ─── Fetch ALL data (client-side filtering) ────────────────────────────────
  const fetchFinancialPeriods = async () => {
    try {
      const result = await dispatch(getFinancialPeriods({ page: 1, per_page: 10 }));
      if (getFinancialPeriods.fulfilled.match(result)) {
        const response = result.payload;
        const data = response?.data || response;
        const paginationData = data?.meta || data?.pagination || financialPeriodsPagination || null;
        const total =
          paginationData?.total ||
          (Array.isArray(data?.data) ? data.data.length : Array.isArray(data) ? data.length : 0);

        if (total > 0) {
          const allResult = await dispatch(getFinancialPeriods({ page: 1, per_page: total }));
          if (getFinancialPeriods.fulfilled.match(allResult)) {
            const allResponse = allResult.payload;
            const allData = allResponse?.data || allResponse;
            let periodsList = [];
            if (Array.isArray(allData?.data)) {
              periodsList = allData.data;
            } else if (Array.isArray(allData)) {
              periodsList = allData;
            }
            setAllFinancialPeriods(periodsList);
          }
        } else {
          setAllFinancialPeriods([]);
        }
      }
    } catch (err) {
    }
  };

  useEffect(() => {
    fetchFinancialPeriods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, i18n.language]);

  useEffect(() => {
    dispatch(clearSuccess());
    dispatch(clearError());
  }, [dispatch]);

  // ─── Toast: success ────────────────────────────────────────────────────────
  useEffect(() => {
    if (success) {
      const now = Date.now();
      const last = lastSuccessRef.current;
      if (!last.message || last.message !== success || now - last.time > 2000) {
        toast.success(success, { rtl: isRTL, position: 'top-right', autoClose: 3000 });
        lastSuccessRef.current = { message: success, time: now };
      }
      if (
        success.includes('إضافة') || success.includes('تم إضافة') ||
        success.includes('Add') || success.includes('added')
      ) {
        fetchFinancialPeriods();
      }
      dispatch(clearSuccess());
    }
  }, [success, dispatch, isRTL]);

  // ─── Toast: error ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { rtl: isRTL, position: 'top-right', autoClose: 5000 });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [error, dispatch, isRTL]);

  // ─── Computed: filter + sort (client-side) ─────────────────────────────────
  const filteredFinancialPeriods = useMemo(() => {
    let result = allFinancialPeriods || [];

    // Text search — Arabic + English, case-insensitive, trim extra spaces
    if (debouncedSearch) {
      const term = convertArabicNumbersToEnglish(debouncedSearch)
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
      result = result.filter((p) => {
        const check = (v) =>
          convertArabicNumbersToEnglish(String(v || '')).toLowerCase().includes(term);
        return check(p.name) || check(p.status) || check(p.created_by) ||
          check(p.start_date) || check(p.end_date);
      });
    }

    // Status filter
    if (statusFilter) {
      result = result.filter(
        (p) => (p.status || '').toLowerCase() === statusFilter.toLowerCase()
      );
    }

    // Date range filter (start_date >= dateFrom, end_date <= dateTo)
    if (dateFrom) {
      result = result.filter((p) => p.start_date && p.start_date >= dateFrom);
    }
    if (dateTo) {
      result = result.filter((p) => p.end_date && p.end_date <= dateTo);
    }

    // Sort
    if (sortField) {
      result = [...result].sort((a, b) => {
        let aVal, bVal;
        if (sortField === SORT_FIELDS.index) {
          // Sort by original record ID for stable ordering
          aVal = a.id ?? 0;
          bVal = b.id ?? 0;
        } else {
          aVal = a[sortField] ?? '';
          bVal = b[sortField] ?? '';
        }
        if (typeof aVal === 'string') aVal = aVal.toLowerCase();
        if (typeof bVal === 'string') bVal = bVal.toLowerCase();
        if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [allFinancialPeriods, debouncedSearch, statusFilter, dateFrom, dateTo, sortField, sortOrder]);

  // ─── Pagination over filtered data ─────────────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(filteredFinancialPeriods.length / perPage));
  const currentPageSafe = Math.min(currentPage, totalPages);
  const paginatedFinancialPeriods = filteredFinancialPeriods.slice(
    (currentPageSafe - 1) * perPage,
    currentPageSafe * perPage
  );

  // ─── Sort handler ──────────────────────────────────────────────────────────
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field)
      return <FaSort style={{ marginLeft: '4px', opacity: 0.4, verticalAlign: 'middle' }} />;
    return sortOrder === 'asc'
      ? <FaSortUp style={{ marginLeft: '4px', color: '#ffd700', verticalAlign: 'middle' }} />
      : <FaSortDown style={{ marginLeft: '4px', color: '#ffd700', verticalAlign: 'middle' }} />;
  };

  // ─── Filter handlers ───────────────────────────────────────────────────────
  const applyFilters = () => {
    setCurrentPage(1);
    setDebouncedSearch(search); // sync immediately without waiting for debounce timer
  };

  const resetFilters = () => {
    setSearch('');
    setDebouncedSearch('');
    setStatusFilter('');
    setDateFrom('');
    setDateTo('');
    setSortField('');
    setSortOrder('asc');
    setCurrentPage(1);
  };

  // ─── Per-page ──────────────────────────────────────────────────────────────
  const handleApplyPerPage = () => {
    const validValue = Math.max(1, Math.min(perPageInput, 1000));
    setPerPage(validValue);
    setCurrentPage(1);
  };

  // ─── Status helpers ────────────────────────────────────────────────────────
  const isClosed = (status) => (status || '').toLowerCase() === 'closed';
  const isClosable = (status) => (status || '').toLowerCase() === 'open';

  // ─── Delete handlers ────────────────────────────────────────────────────────
  const handleDelete = (period) => {
    if (isClosed(period?.status)) {
      toast.error(
        t('financialPeriods.delete.cannotDeleteClosed', { defaultValue: 'Cannot delete a closed financial period' }),
        { rtl: isRTL }
      );
      return;
    }
    setPeriodToDelete(period);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!periodToDelete) return;
    if (isClosed(periodToDelete?.status)) {
      toast.error(
        t('financialPeriods.delete.cannotDeleteClosed', { defaultValue: 'Cannot delete a closed financial period' }),
        { rtl: isRTL }
      );
      setShowDeleteModal(false);
      setPeriodToDelete(null);
      return;
    }
    const idToDelete = periodToDelete.id;
    setShowDeleteModal(false);
    setPeriodToDelete(null);
    try {
      await dispatch(deleteFinancialPeriod(idToDelete)).unwrap();
      setAllFinancialPeriods((prev) => prev.filter((p) => p.id !== idToDelete));
    } catch (_) {
      // error shown by useEffect
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setPeriodToDelete(null);
  };

  // ─── Close-period handlers ─────────────────────────────────────────────────
  const handleClosePeriod = (period) => {
    setPeriodToClose(period);
    setShowCloseModal(true);
  };

  const confirmClose = async () => {
    if (!periodToClose) return;
    await dispatch(closeFinancialPeriod(periodToClose.id));
    setShowCloseModal(false);
    setPeriodToClose(null);
    fetchFinancialPeriods();
  };

  const cancelClose = () => {
    setShowCloseModal(false);
    setPeriodToClose(null);
  };

  // ─── Helpers ───────────────────────────────────────────────────────────────
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric', month: '2-digit', day: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const statusColors = {
    open: '#0d6efd',
    closed: '#dc3545',
    'soft closed': '#845ef7',
    pending: '#f59f00',
    default: '#6c757d',
  };

  const renderStatusBadge = (status) => {
    if (!status) return '-';
    const key = (status || '').toLowerCase();
    const backgroundColor = statusColors[key] || statusColors.default;
    return (
      <span style={{
        backgroundColor, color: 'white', padding: '6px 14px',
        borderRadius: '20px', fontSize: '12px', fontWeight: 'bold', display: 'inline-block',
      }}>
        {status}
      </span>
    );
  };

  // ─── PDF: Dynamic Pagination — shared engine ──────────────────────────────
  const buildDynamicPdfChunks = (periods) => {
    if (!Array.isArray(periods) || periods.length === 0) {
      setPdfChunks([]);
      return [];
    }
    const elements = {
      topHeader: document.getElementById('financial-periods-pdf-measure-top-header'),
      thead: document.getElementById('financial-periods-pdf-measure-thead'),
      footer: document.getElementById('financial-periods-pdf-measure-footer'),
      tbody: document.getElementById('financial-periods-pdf-measure-tbody'),
    };
    // Dynamic fallback — no magic numbers
    const estimatedRowHeight = 30;
    const estimatedAvailable = 1033 - 18 - 10 - 180 - 32 - 120 - 15;
    const dynamicFallbackSize = Math.max(5, Math.floor(estimatedAvailable / estimatedRowHeight));

    const chunks = calculatePdfPagination(periods, elements, {
      pageHeight: 1033,  // A4 inner bordered div @ 96dpi
      containerTopPadding: 18,
      containerBottomPadding: 10,
      safeMargin: 15,
      fallbackSize: dynamicFallbackSize,
      defaultHeaderHeight: 180,
      defaultTableHeaderHeight: 32,
      defaultFooterHeight: 120,  // matches fixed placeholder height
      defaultRowHeight: 28,
    });
    setPdfChunks(chunks);
    return chunks;
  };

  const prepareAllFinancialPeriodsPdfData = async () => {
    if (!filteredFinancialPeriods || filteredFinancialPeriods.length === 0) {
      toast.error(
        t('financialPeriods.preview.noData', { defaultValue: 'No data available for preview' }),
        { rtl: isRTL }
      );
      return [];
    }
    await new Promise((resolve) => setTimeout(resolve, 80));
    await new Promise((resolve) => requestAnimationFrame(resolve));
    await new Promise((resolve) => requestAnimationFrame(resolve));
    return buildDynamicPdfChunks(filteredFinancialPeriods);
  };

  // ─── PDF: Preview single ───────────────────────────────────────────────────
  const handlePreviewSingle = (period) => {
    if (!period) {
      toast.error(
        t('financialPeriods.preview.noData', { defaultValue: 'No data available for this financial period' }),
        { rtl: isRTL }
      );
      return;
    }
    setPeriodForPrint(period);
    setPdfType('single');
    setIsPreviewOpen(true);
  };

  // ─── PDF: Preview all ─────────────────────────────────────────────────────
  const handlePreviewAll = async () => {
    const chunks = await prepareAllFinancialPeriodsPdfData();
    if (!chunks || chunks.length === 0) return;
    setPdfType('all');
    setIsPreviewOpen(true);
  };

  // ─── PDF: Print single ────────────────────────────────────────────────────
  const handlePrintFinancialPeriod = async (period) => {
    if (!period) {
      toast.error(t('financialPeriods.print.noData', { defaultValue: 'No data available' }), { rtl: isRTL });
      return;
    }
    try {
      setPeriodForPrint(period);
      setPdfType('single');

      await waitForRender();
      setIsGeneratingPdf(true);

      // Shared PDF Engine — capture + assembly
      await generateSingleElementPdf({
        elementId: `financial-period-pdf-${period.id}`,
        fileName: datedFileName(`ACE_FinancialPeriod_${period.name || period.id}`),
        isRTL,
      });
      toast.success(t('financialPeriods.print.success', { defaultValue: 'PDF downloaded successfully' }), { rtl: isRTL });
    } catch (err) {
      toast.error(t('financialPeriods.print.error', { defaultValue: 'An error occurred while creating the PDF' }), { rtl: isRTL });
    } finally {
      setIsGeneratingPdf(false);
      setPeriodForPrint(null);
      setPdfType(null);
    }
  };

  // ─── PDF: Print all — always re-builds from fresh filtered data ───────────
  const handlePrintAll = async () => {
    if (!filteredFinancialPeriods || filteredFinancialPeriods.length === 0) {
      toast.error(t('financialPeriods.print.noData', { defaultValue: 'لا توجد بيانات للطباعة' }), { rtl: isRTL });
      return;
    }
    try {
      setIsGeneratingPdf(true);
      // Always re-fetch — never reuse stale pdfChunks from old filter state
      const chunks = await prepareAllFinancialPeriodsPdfData();
      if (!chunks || chunks.length === 0) {
        setIsGeneratingPdf(false);
        return;
      }
      setPdfType('all');
      await waitForRender();

      // Shared PDF Engine — capture + assembly (one A4 page per chunk)
      await generatePdfFromElementIds({
        elementIds: chunks.map((_, i) => `financial-periods-pdf-page-${i}`),
        fileName: datedFileName('ACE_FinancialPeriods_All'),
        isRTL,
      });
      toast.success(t('financialPeriods.print.success', { defaultValue: 'PDF downloaded successfully' }), { rtl: isRTL });
    } catch (err) {
      toast.error(t('financialPeriods.print.error', { defaultValue: 'An error occurred while creating the PDF' }), { rtl: isRTL });
    } finally {
      setIsGeneratingPdf(false);
      setPdfType(null);
      setPdfChunks([]);
    }
  };

  // ─── Excel: export single ─────────────────────────────────────────────────
  const handleExportFinancialPeriodToExcel = (period) => {
    const data = [{
      [t('financialPeriods.table.name', { defaultValue: 'Period Name' })]: period.name || '—',
      [t('financialPeriods.table.startDate', { defaultValue: 'Start Date' })]: formatDate(period.start_date),
      [t('financialPeriods.table.endDate', { defaultValue: 'End Date' })]: formatDate(period.end_date),
      [t('financialPeriods.table.status', { defaultValue: 'Status' })]: period.status || '—',
      [t('financialPeriods.table.createdBy', { defaultValue: 'Created By' })]: period.created_by || '—',
      [t('financialPeriods.table.createdAt', { defaultValue: 'Created At' })]: formatDate(period.created_at),
    }];
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t('financialPeriods.excel.sheetName', { defaultValue: 'Financial Period' }));
    XLSX.writeFile(
      wb,
      `${t('financialPeriods.excel.fileName', { defaultValue: 'FinancialPeriod' })}_${period.name || period.id}_${new Date().toISOString().split('T')[0]}.xlsx`
    );
    toast.success(t('financialPeriods.export.success', { defaultValue: 'Data exported successfully' }), { rtl: isRTL });
  };

  // ─── Excel: export all filtered data ─────────────────────────────────────
  const handleExportAllToExcel = () => {
    if (!filteredFinancialPeriods || filteredFinancialPeriods.length === 0) {
      toast.error(t('financialPeriods.export.noData', { defaultValue: 'لا توجد بيانات للتصدير' }), { rtl: isRTL });
      return;
    }
    // Export filteredFinancialPeriods (not paginated Redux state) with sequential # column
    const data = filteredFinancialPeriods.map((period, index) => ({
      'م': index + 1,
      [t('financialPeriods.tableHeaders.name', { defaultValue: 'اسم الفترة' })]: period.name || '—',
      [t('financialPeriods.tableHeaders.startDate', { defaultValue: 'تاريخ البداية' })]: formatDate(period.start_date),
      [t('financialPeriods.tableHeaders.endDate', { defaultValue: 'تاريخ النهاية' })]: formatDate(period.end_date),
      [t('financialPeriods.tableHeaders.status', { defaultValue: 'الحالة' })]: period.status || '—',
      [t('financialPeriods.tableHeaders.createdBy', { defaultValue: 'أنشئت بواسطة' })]: period.created_by || '—',
      [t('financialPeriods.tableHeaders.createdAt', { defaultValue: 'تاريخ الإنشاء' })]: formatDate(period.created_at),
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, isRTL ? 'الفترات المالية' : 'Financial Periods');
    XLSX.writeFile(
      wb,
      `${isRTL ? 'الفترات_المالية' : 'FinancialPeriods'}_${new Date().toISOString().split('T')[0]}.xlsx`
    );
    toast.success(t('financialPeriods.export.success', { defaultValue: 'تم تصدير البيانات بنجاح' }), { rtl: isRTL });
  };

  // ─── Loading state ─────────────────────────────────────────────────────────
  if (isLoading && (!allFinancialPeriods || allFinancialPeriods.length === 0)) {
    return (
      <div style={{
        padding: '30px', backgroundColor: '#14263b', minHeight: 'calc(100vh - 80px)',
        color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', direction: dir,
      }}>
        {t('common.loading', { defaultValue: 'Loading...' })}
      </div>
    );
  }

  // ─── Shared sortable-th style ─────────────────────────────────────────────
  const thStyle = (align = 'center') => ({
    padding: '18px 16px', textAlign: align, color: 'white', fontWeight: 'bold',
    fontSize: '14px', verticalAlign: 'middle', cursor: 'pointer',
    userSelect: 'none', whiteSpace: 'nowrap',
  });

  const hasActiveFilters = !!(debouncedSearch || statusFilter || dateFrom || dateTo || sortField);

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <div style={{
        padding: '30px', backgroundColor: '#14263b',
        minHeight: 'calc(100vh - 80px)', color: 'white', direction: dir,
      }}>

        {/* ── Header row ─────────────────────────────────────────────────── */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginBottom: '16px', flexWrap: 'wrap', gap: '15px',
          }}>
            <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0, textAlign }}>
              {t('financialPeriods.title', { defaultValue: 'Financial Periods' })}
            </h1>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button
                onClick={handlePreviewAll}
                disabled={!filteredFinancialPeriods || filteredFinancialPeriods.length === 0}
                style={{
                  backgroundColor: !filteredFinancialPeriods || filteredFinancialPeriods.length === 0 ? '#666' : '#444',
                  color: 'white', border: 'none', padding: '12px 20px', borderRadius: '8px',
                  fontSize: '14px', fontWeight: 'bold',
                  cursor: !filteredFinancialPeriods || filteredFinancialPeriods.length === 0 ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: '8px',
                  flexDirection: isRTL ? 'row' : 'row-reverse',
                }}
              >
                <FaEye /> {t('financialPeriods.preview.all', { defaultValue: 'Preview PDF All' })}
              </button>
              <button
                onClick={handlePrintAll}
                disabled={isGeneratingPdf || !filteredFinancialPeriods || filteredFinancialPeriods.length === 0}
                style={{
                  backgroundColor: isGeneratingPdf || !filteredFinancialPeriods || filteredFinancialPeriods.length === 0 ? '#666' : '#1f5ff1',
                  color: 'white', border: 'none', padding: '12px 20px', borderRadius: '8px',
                  fontSize: '14px', fontWeight: 'bold',
                  cursor: isGeneratingPdf || !filteredFinancialPeriods || filteredFinancialPeriods.length === 0 ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: '8px',
                  flexDirection: isRTL ? 'row' : 'row-reverse', opacity: isGeneratingPdf ? 0.6 : 1,
                }}
              >
                <FaPrint /> {t('financialPeriods.print.all', { defaultValue: 'Print PDF All' })}
              </button>
              <button
                onClick={handleExportAllToExcel}
                disabled={!filteredFinancialPeriods || filteredFinancialPeriods.length === 0}
                style={{
                  backgroundColor: !filteredFinancialPeriods || filteredFinancialPeriods.length === 0 ? '#666' : '#27ae60',
                  color: 'white', border: 'none', padding: '12px 20px', borderRadius: '8px',
                  fontSize: '14px', fontWeight: 'bold',
                  cursor: !filteredFinancialPeriods || filteredFinancialPeriods.length === 0 ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: '8px',
                  flexDirection: isRTL ? 'row' : 'row-reverse',
                }}
              >
                <FaFileExcel /> {t('financialPeriods.export.all', { defaultValue: 'Export Excel' })}
              </button>
              <button
                onClick={() => navigate('/financial-periods/add')}
                style={{
                  backgroundColor: '#AC2000', color: 'white', border: 'none',
                  padding: '12px 20px', borderRadius: '8px', fontSize: '14px',
                  fontWeight: 'bold', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: '8px',
                  flexDirection: isRTL ? 'row' : 'row-reverse',
                }}
              >
                <FaPlus /> {t('financialPeriods.add', { defaultValue: 'Add Financial Period' })}
              </button>
            </div>
          </div>

          {/* ── Search bar ────────────────────────────────────────────────── */}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', flex: '1 1 280px', minWidth: '200px' }}>
              <FaSearch style={{
                position: 'absolute', top: '50%',
                [isRTL ? 'right' : 'left']: '12px',
                transform: 'translateY(-50%)', color: '#9ca3af',
                pointerEvents: 'none', zIndex: 1,
              }} />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('common.search', { defaultValue: 'Search...' })}
                style={{
                  width: '100%',
                  padding: isRTL ? '10px 36px 10px 12px' : '10px 12px 10px 36px',
                  backgroundColor: '#1a1f2e', border: '1px solid #333',
                  borderRadius: '8px', color: 'white', fontSize: '14px',
                  boxSizing: 'border-box', direction: dir,
                }}
              />
            </div>

            <button
              onClick={() => setShowFilters((prev) => !prev)}
              style={{
                backgroundColor: showFilters ? '#AC2000' : '#1b3350',
                color: 'white', border: '1px solid #333', padding: '10px 16px',
                borderRadius: '8px', cursor: 'pointer', fontSize: '13px',
                fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px',
                flexDirection: isRTL ? 'row' : 'row-reverse',
              }}
            >
              <FaFilter /> {t('common.filters', { defaultValue: 'Filters' })}
            </button>

            {hasActiveFilters && (
              <button
                onClick={resetFilters}
                style={{
                  backgroundColor: '#444', color: 'white', border: '1px solid #555',
                  padding: '10px 14px', borderRadius: '8px', cursor: 'pointer',
                  fontSize: '13px', whiteSpace: 'nowrap',
                }}
              >
                {t('common.reset', { defaultValue: 'Reset' })}
              </button>
            )}
          </div>

          {/* ── Filter panel ──────────────────────────────────────────────── */}
          {showFilters && (
            <div style={{
              backgroundColor: '#1b3350', border: '1px solid #333', borderRadius: '10px',
              padding: '16px', marginBottom: '16px',
              display: 'flex', flexWrap: 'wrap', gap: '14px', alignItems: 'flex-end', direction: dir,
            }}>
              {/* Status */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '180px' }}>
                <label style={{ color: '#9ca3af', fontSize: '12px' }}>
                  {t('financialPeriods.table.status', { defaultValue: 'Status' })}
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                  style={{
                    backgroundColor: '#1a1f2e', border: '1px solid #333', color: 'white',
                    padding: '8px 10px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer',
                  }}
                >
                  <option value="">{t('common.all', { defaultValue: 'All' })}</option>
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              {/* Date From */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '160px' }}>
                <label style={{ color: '#9ca3af', fontSize: '12px' }}>
                  {t('financialPeriods.filters.dateFrom', { defaultValue: 'Start Date From' })}
                </label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => { setDateFrom(e.target.value); setCurrentPage(1); }}
                  style={{
                    backgroundColor: '#1a1f2e', border: '1px solid #333', color: 'white',
                    padding: '8px 10px', borderRadius: '6px', fontSize: '13px', colorScheme: 'dark',
                  }}
                />
              </div>

              {/* Date To */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '160px' }}>
                <label style={{ color: '#9ca3af', fontSize: '12px' }}>
                  {t('financialPeriods.filters.dateTo', { defaultValue: 'End Date To' })}
                </label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => { setDateTo(e.target.value); setCurrentPage(1); }}
                  style={{
                    backgroundColor: '#1a1f2e', border: '1px solid #333', color: 'white',
                    padding: '8px 10px', borderRadius: '6px', fontSize: '13px', colorScheme: 'dark',
                  }}
                />
              </div>

              {/* Apply / Reset */}
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button
                  onClick={applyFilters}
                  style={{
                    backgroundColor: '#AC2000', color: 'white', border: 'none',
                    padding: '9px 18px', borderRadius: '8px', cursor: 'pointer',
                    fontSize: '13px', fontWeight: 'bold',
                  }}
                >
                  {t('common.apply', { defaultValue: 'Apply' })}
                </button>
                <button
                  onClick={resetFilters}
                  style={{
                    backgroundColor: '#444', color: 'white', border: '1px solid #555',
                    padding: '9px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px',
                  }}
                >
                  {t('common.reset', { defaultValue: 'Reset' })}
                </button>
              </div>
            </div>
          )}

          {/* ── Per-page control ──────────────────────────────────────────── */}
          <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <label style={{ color: 'white', fontSize: '14px', whiteSpace: 'nowrap' }}>
              {t('common.perPage', { defaultValue: 'عدد السجلات' })}:
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <button
                onClick={() => setPerPageInput(Math.max(1, perPageInput - 1))}
                style={{
                  backgroundColor: '#1b3350', color: 'white', border: '1px solid #333',
                  padding: '6px 10px', borderRadius: '6px 0 0 6px', cursor: 'pointer',
                  fontSize: '14px', fontWeight: 'bold', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', minWidth: '32px',
                }}
              >−</button>
              <input
                type="number" min="1" max="1000" value={perPageInput}
                onChange={(e) => {
                  const value = parseInt(e.target.value) || 1;
                  setPerPageInput(Math.max(1, Math.min(value, 1000)));
                }}
                style={{
                  width: '70px', padding: '6px 10px', backgroundColor: '#1a1f2e',
                  border: '1px solid #333', borderLeft: 'none', borderRight: 'none',
                  borderRadius: 0, color: 'white', fontSize: '14px', textAlign: 'center',
                }}
              />
              <button
                onClick={() => setPerPageInput(Math.min(1000, perPageInput + 1))}
                style={{
                  backgroundColor: '#1b3350', color: 'white', border: '1px solid #333',
                  padding: '6px 10px', borderRadius: '0 6px 6px 0', cursor: 'pointer',
                  fontSize: '14px', fontWeight: 'bold', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', minWidth: '32px',
                }}
              >+</button>
            </div>
            <button
              onClick={handleApplyPerPage}
              style={{
                backgroundColor: '#AC2000', color: 'white', border: 'none',
                padding: '10px 16px', borderRadius: '8px', cursor: 'pointer',
                fontSize: '13px', fontWeight: 'bold', whiteSpace: 'nowrap',
              }}
            >
              {t('common.apply', { defaultValue: 'تطبيق' })}
            </button>

            {/* Results count */}
            <span style={{ color: '#9ca3af', fontSize: '13px' }}>
              {filteredFinancialPeriods.length} {t('common.records', { defaultValue: 'records' })}
              {hasActiveFilters && allFinancialPeriods.length !== filteredFinancialPeriods.length
                ? ` ${t('common.of', { defaultValue: 'of' })} ${allFinancialPeriods.length}`
                : ''}
            </span>
          </div>
        </div>

        {/* ── Table ─────────────────────────────────────────────────────────── */}
        {paginatedFinancialPeriods && paginatedFinancialPeriods.length > 0 ? (
          <div style={{
            backgroundColor: '#1b3350', border: '1px solid #333',
            borderRadius: '12px', overflow: 'hidden', direction: dir,
          }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%', borderCollapse: 'separate', borderSpacing: 0,
                minWidth: '1000px', tableLayout: 'fixed',
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#AC2000' }}>
                    <th onClick={() => handleSort(SORT_FIELDS.index)}
                      style={{ ...thStyle('center'), width: '60px', minWidth: '60px' }}>
                      {t('common.serialNumber', { defaultValue: '#' })} {getSortIcon(SORT_FIELDS.index)}
                    </th>
                    <th onClick={() => handleSort(SORT_FIELDS.name)}
                      style={{ ...thStyle(textAlign), width: '220px', direction: dir }}>
                      {t('financialPeriods.table.name', { defaultValue: 'Period Name' })} {getSortIcon(SORT_FIELDS.name)}
                    </th>
                    <th onClick={() => handleSort(SORT_FIELDS.start_date)}
                      style={{ ...thStyle('center'), width: '160px' }}>
                      {t('financialPeriods.table.startDate', { defaultValue: 'Start Date' })} {getSortIcon(SORT_FIELDS.start_date)}
                    </th>
                    <th onClick={() => handleSort(SORT_FIELDS.end_date)}
                      style={{ ...thStyle('center'), width: '160px' }}>
                      {t('financialPeriods.table.endDate', { defaultValue: 'End Date' })} {getSortIcon(SORT_FIELDS.end_date)}
                    </th>
                    <th onClick={() => handleSort(SORT_FIELDS.status)}
                      style={{ ...thStyle('center'), width: '140px' }}>
                      {t('financialPeriods.table.status', { defaultValue: 'Status' })} {getSortIcon(SORT_FIELDS.status)}
                    </th>
                    <th onClick={() => handleSort(SORT_FIELDS.created_by)}
                      style={{ ...thStyle(textAlign), width: '180px', direction: dir }}>
                      {t('financialPeriods.table.createdBy', { defaultValue: 'Created By' })} {getSortIcon(SORT_FIELDS.created_by)}
                    </th>
                    <th onClick={() => handleSort(SORT_FIELDS.created_at)}
                      style={{ ...thStyle('center'), width: '160px' }}>
                      {t('financialPeriods.table.createdAt', { defaultValue: 'Created At' })} {getSortIcon(SORT_FIELDS.created_at)}
                    </th>
                    <th style={{ ...thStyle('center'), width: '240px', cursor: 'default' }}>
                      {t('financialPeriods.table.actions', { defaultValue: 'Actions' })}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedFinancialPeriods.map((period, index) => {
                    const recordIndex = (currentPageSafe - 1) * perPage + index + 1;
                    return (
                      <tr
                        key={period.id}
                        style={{
                          borderBottom: '1px solid #333',
                          backgroundColor: index % 2 === 0 ? '#1b3350' : '#1a1f2e',
                          transition: 'background-color 0.2s',
                        }}
                        onMouseEnter={(ev) => {
                          const row = ev.target.closest('tr');
                          if (row) row.style.backgroundColor = '#2a3441';
                        }}
                        onMouseLeave={(ev) => {
                          const row = ev.target.closest('tr');
                          if (row) row.style.backgroundColor = index % 2 === 0 ? '#1b3350' : '#1a1f2e';
                        }}
                      >
                        <td style={{ padding: '18px 16px', textAlign: 'center', color: 'white', fontSize: '14px', verticalAlign: 'middle' }}>
                          {recordIndex}
                        </td>
                        <td style={{ padding: '18px 16px', textAlign, color: 'white', fontSize: '14px', direction: dir, verticalAlign: 'middle' }}>
                          {period.name || '—'}
                        </td>
                        <td style={{ padding: '18px 16px', textAlign: 'center', color: 'white', fontSize: '14px', verticalAlign: 'middle' }}>
                          {formatDate(period.start_date)}
                        </td>
                        <td style={{ padding: '18px 16px', textAlign: 'center', color: 'white', fontSize: '14px', verticalAlign: 'middle' }}>
                          {formatDate(period.end_date)}
                        </td>
                        <td style={{ padding: '18px 16px', textAlign: 'center', verticalAlign: 'middle' }}>
                          {renderStatusBadge(period.status)}
                        </td>
                        <td style={{ padding: '18px 16px', textAlign, color: 'white', fontSize: '14px', direction: dir, verticalAlign: 'middle' }}>
                          {period.created_by || '—'}
                        </td>
                        <td style={{ padding: '18px 16px', textAlign: 'center', color: 'white', fontSize: '14px', verticalAlign: 'middle' }}>
                          {formatDate(period.created_at)}
                        </td>
                        <td style={{ padding: '18px 16px', textAlign: 'center', verticalAlign: 'middle' }}>
                          <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', flexWrap: 'nowrap' }}>
                            {/* Preview */}
                            <button
                              title={t('financialPeriods.actions.view', { defaultValue: 'View Details' })}
                              onClick={() => handlePreviewSingle(period)}
                              style={{ backgroundColor: '#666', color: 'white', border: 'none', padding: '8px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}
                            >
                              <FaEye />
                            </button>
                            {/* Print */}
                            <button
                              title={t('financialPeriods.actions.print', { defaultValue: 'Print' })}
                              onClick={() => handlePrintFinancialPeriod(period)}
                              disabled={isGeneratingPdf}
                              style={{ backgroundColor: isGeneratingPdf ? '#666' : '#1f5ff1', color: 'white', border: 'none', padding: '8px 12px', borderRadius: '6px', cursor: isGeneratingPdf ? 'not-allowed' : 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px', opacity: isGeneratingPdf ? 0.6 : 1 }}
                            >
                              <FaPrint />
                            </button>
                            {/* Excel */}
                            <button
                              title={t('financialPeriods.actions.export', { defaultValue: 'Export Excel' })}
                              onClick={() => handleExportFinancialPeriodToExcel(period)}
                              style={{ backgroundColor: '#28a745', color: 'white', border: 'none', padding: '8px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}
                            >
                              <FaFileExcel />
                            </button>
                            {/* Edit */}
                            {(() => {
                              const closed = isClosed(period.status);
                              return (
                                <button
                                  title={t('financialPeriods.actions.edit', { defaultValue: 'Edit' })}
                                  onClick={() => { if (!closed) navigate(`/financial-periods/edit/${period.id}`); }}
                                  disabled={closed}
                                  style={{ backgroundColor: closed ? '#555' : '#0bb564', color: 'white', border: 'none', padding: '8px 12px', borderRadius: '6px', cursor: closed ? 'not-allowed' : 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px', opacity: closed ? 0.6 : 1 }}
                                >
                                  <FaEdit />
                                </button>
                              );
                            })()}
                            {/* Close */}
                            <button
                              title={t('financialPeriods.actions.close', { defaultValue: 'Close Period' })}
                              onClick={() => handleClosePeriod(period)}
                              disabled={!isClosable(period.status)}
                              style={{ backgroundColor: isClosable(period.status) ? '#845ef7' : '#555', color: 'white', border: 'none', padding: '8px 12px', borderRadius: '6px', cursor: isClosable(period.status) ? 'pointer' : 'not-allowed', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px', opacity: isClosable(period.status) ? 1 : 0.6 }}
                            >
                              <FaLock />
                            </button>
                            {/* Delete */}
                            {(() => {
                              const closed = isClosed(period.status);
                              return (
                                <button
                                  title={closed
                                    ? t('financialPeriods.actions.cannotDeleteClosed', { defaultValue: 'Cannot delete closed period' })
                                    : t('financialPeriods.actions.delete', { defaultValue: 'Delete' })
                                  }
                                  onClick={() => { if (!closed) handleDelete(period); }}
                                  disabled={closed}
                                  style={{ backgroundColor: closed ? '#555' : '#dc3545', color: 'white', border: 'none', padding: '8px 12px', borderRadius: '6px', cursor: closed ? 'not-allowed' : 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px', opacity: closed ? 0.6 : 1 }}
                                >
                                  <FaTrash />
                                </button>
                              );
                            })()}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* ── Pagination ──────────────────────────────────────────────── */}
            {totalPages > 1 && (() => {
              const pages = [];
              for (let i = 1; i <= totalPages; i++) {
                if (i === 1 || i === totalPages || (i >= currentPageSafe - 2 && i <= currentPageSafe + 2)) {
                  pages.push(i);
                } else if (i === currentPageSafe - 3 || i === currentPageSafe + 3) {
                  pages.push('...');
                }
              }
              return (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '20px', paddingBottom: '20px', borderTop: '1px solid #333', paddingTop: '20px' }}>
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPageSafe - 1))}
                    disabled={currentPageSafe === 1}
                    style={{ padding: '8px 12px', backgroundColor: currentPageSafe === 1 ? '#333' : '#AC2000', color: 'white', border: 'none', borderRadius: '4px', cursor: currentPageSafe === 1 ? 'not-allowed' : 'pointer' }}
                  >
                    {t('common.previous', { defaultValue: 'Previous' })}
                  </button>
                  {pages.map((p, idx) =>
                    p === '...' ? (
                      <span key={`ellipsis-${idx}`} style={{ padding: '8px', color: 'white' }}>...</span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setCurrentPage(p)}
                        style={{ padding: '8px 12px', backgroundColor: p === currentPageSafe ? '#AC2000' : '#1a1f2e', color: 'white', border: '1px solid #333', borderRadius: '4px', cursor: 'pointer' }}
                      >
                        {p}
                      </button>
                    )
                  )}
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPageSafe + 1))}
                    disabled={currentPageSafe === totalPages}
                    style={{ padding: '8px 12px', backgroundColor: currentPageSafe === totalPages ? '#333' : '#AC2000', color: 'white', border: 'none', borderRadius: '4px', cursor: currentPageSafe === totalPages ? 'not-allowed' : 'pointer' }}
                  >
                    {t('common.next', { defaultValue: 'Next' })}
                  </button>
                </div>
              );
            })()}
          </div>
        ) : (
          <div style={{
            backgroundColor: '#1b3350', border: '1px solid #333', borderRadius: '12px',
            padding: '40px', textAlign: 'center', color: '#9ca3af', fontSize: '16px', direction: dir,
          }}>
            {hasActiveFilters
              ? t('common.noResults', { defaultValue: 'No results match your search criteria' })
              : t('financialPeriods.noData', { defaultValue: 'No financial periods found' })
            }
          </div>
        )}

        {/* ── Delete Modal ────────────────────────────────────────────────── */}
        {showDeleteModal && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
            <div style={{ backgroundColor: '#1f2937', border: '1px solid #333', borderRadius: '12px', padding: '24px', width: '90%', maxWidth: '420px', direction: dir }}>
              <h3 style={{ color: 'white', marginBottom: '12px', fontSize: '18px', textAlign, direction: dir }}>{t('financialPeriods.delete.title', { defaultValue: 'Confirm Delete' })}</h3>
              <p style={{ color: '#ccc', marginBottom: '8px', fontSize: '14px', textAlign, direction: dir }}>
                {t('financialPeriods.delete.confirm', { defaultValue: 'Are you sure you want to delete the financial period' })} {periodToDelete?.name}?
              </p>
              <p style={{ color: '#ef4444', marginBottom: '20px', fontSize: '12px', textAlign, direction: dir }}>{t('financialPeriods.delete.irreversible', { defaultValue: 'This action cannot be undone.' })}</p>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexDirection: isRTL ? 'row' : 'row-reverse' }}>
                <button onClick={cancelDelete} style={{ backgroundColor: '#666', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 16px', cursor: 'pointer' }}>
                  {t('financialPeriods.delete.cancel', { defaultValue: 'Cancel' })}
                </button>
                <button onClick={confirmDelete} style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 16px', cursor: 'pointer' }}>
                  {t('financialPeriods.delete.confirmButton', { defaultValue: 'Delete' })}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Close Modal ─────────────────────────────────────────────────── */}
        {showCloseModal && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
            <div style={{ backgroundColor: '#1f2937', border: '1px solid #333', borderRadius: '12px', padding: '24px', width: '90%', maxWidth: '420px', direction: dir }}>
              <h3 style={{ color: 'white', marginBottom: '12px', fontSize: '18px', textAlign, direction: dir }}>{t('financialPeriods.close.title', { defaultValue: 'Close Financial Period' })}</h3>
              <p style={{ color: '#ccc', marginBottom: '8px', fontSize: '14px', textAlign, direction: dir }}>
                {t('financialPeriods.close.confirm', { defaultValue: 'Are you sure you want to close the financial period' })} {periodToClose?.name}?
              </p>
              <p style={{ color: '#f59f00', marginBottom: '20px', fontSize: '12px', textAlign, direction: dir }}>
                {t('financialPeriods.close.warning', { defaultValue: 'After closing, you will not be able to record new transactions within this period.' })}
              </p>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexDirection: isRTL ? 'row' : 'row-reverse' }}>
                <button onClick={cancelClose} style={{ backgroundColor: '#666', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 16px', cursor: 'pointer' }}>
                  {t('financialPeriods.close.cancel', { defaultValue: 'Cancel' })}
                </button>
                <button onClick={confirmClose} style={{ backgroundColor: '#845ef7', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 16px', cursor: 'pointer' }}>
                  {t('financialPeriods.close.confirmButton', { defaultValue: 'Close' })}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Hidden PDF: single ──────────────────────────────────────────── */}
        {periodForPrint && pdfType === 'single' && (
          <div
            id={`financial-period-pdf-${periodForPrint.id}`}
            aria-hidden="true"
            style={{
              position: 'absolute', left: '-9999px', top: 0,
              width: '794px', height: '1123px', backgroundColor: '#ffffff',
              color: '#001437', padding: '24px', boxSizing: 'border-box',
              fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
              direction: isRTL ? 'rtl' : 'ltr',
            }}
          >
            <div style={{ border: '2px solid #000', height: '100%', padding: '18px', position: 'relative', display: 'flex', flexDirection: 'column' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundImage: `url(${headerLogo})`, backgroundSize: '95%', backgroundPosition: 'center center', backgroundRepeat: 'no-repeat', opacity: 0.08, zIndex: 0, pointerEvents: 'none' }} />
              <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <img src={`${publicUrl}/HeaderAce.png`} alt="Header" style={{ width: '100%', height: 'auto', objectFit: 'contain', marginBottom: '8px', borderBottom: '4px double #000', paddingBottom: '8px', flexShrink: 0 }} crossOrigin="anonymous" />
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '120px' }}>
                  <div style={{ marginTop: '12px', textAlign: 'center', marginBottom: '20px' }}>
                    <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#000080', margin: 0, direction: dir }}>{t('financialPeriods.print.title', { defaultValue: 'Financial Period Details' })}</h2>
                  </div>
                  <div style={{ border: '1px solid #000', padding: '12px', marginBottom: '12px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', fontSize: '13px', lineHeight: 1.6, direction: dir, textAlign }}>
                      <div><strong>{t('financialPeriods.print.name', { defaultValue: 'Period Name' })}:</strong> {periodForPrint.name || '—'}</div>
                      <div><strong>{t('financialPeriods.print.startDate', { defaultValue: 'Start Date' })}:</strong> {formatDate(periodForPrint.start_date)}</div>
                      <div><strong>{t('financialPeriods.print.endDate', { defaultValue: 'End Date' })}:</strong> {formatDate(periodForPrint.end_date)}</div>
                      <div><strong>{t('financialPeriods.print.status', { defaultValue: 'Status' })}:</strong> {periodForPrint.status || '—'}</div>
                      <div><strong>{t('financialPeriods.print.createdBy', { defaultValue: 'Created By' })}:</strong> {periodForPrint.created_by || '—'}</div>
                      <div><strong>{t('financialPeriods.print.createdAt', { defaultValue: 'Created At' })}:</strong> {formatDate(periodForPrint.created_at)}</div>
                    </div>
                  </div>
                </div>
              </div>
              <div style={{ position: 'absolute', bottom: '10px', left: '18px', right: '18px', flexShrink: 0 }}>
                <img src={`${publicUrl}/FooterAce.png`} alt="Footer" style={{ width: '100%', height: 'auto', objectFit: 'contain', borderTop: '4px double #000', paddingTop: '8px' }} crossOrigin="anonymous" />
              </div>
            </div>
          </div>
        )}

        {/* ── Measurement template (hidden, feeds calculatePdfPagination) ── */}
        <FinancialPeriodsPdfMeasurementTemplate
          periods={filteredFinancialPeriods || []}
          isRTL={isRTL}
          t={t}
          formatDate={formatDate}
          publicUrl={publicUrl}
        />

        {/* ── Hidden PDF: all pages ───────────────────────────────────────── */}
        {pdfType === 'all' && (pdfChunks || []).map((chunk, chunkIndex) => (
          <FinancialPeriodsPdfPageTemplate
            key={chunkIndex}
            idPrefix="financial-periods-pdf-page"
            periods={chunk.items}
            startIndex={chunk.startIndex}
            chunkIndex={chunkIndex}
            totalChunks={pdfChunks?.length || 1}
            isPreview={false}
            isRTL={isRTL}
            t={t}
            formatDate={formatDate}
            publicUrl={publicUrl}
            headerLogo={headerLogo}
            dir={dir}
            textAlign={textAlign}
          />
        ))}

        {/* ── Preview Modal ───────────────────────────────────────────────── */}
        <PdfPreviewModal
          isOpen={
            isPreviewOpen &&
            ((pdfType === 'single' && periodForPrint) ||
              (pdfType === 'all' && filteredFinancialPeriods && filteredFinancialPeriods.length > 0))
          }
          title={
            pdfType === 'single'
              ? t('financialPeriods.preview.single', { defaultValue: 'Financial Period Preview' })
              : t('financialPeriods.preview.all', { defaultValue: 'Financial Periods Preview' })
          }
          onClose={() => {
            setIsPreviewOpen(false);
            setPeriodForPrint(null);
            setPdfType(null);
            setPdfChunks([]);
          }}
          onPrint={pdfType === 'single' ? () => handlePrintFinancialPeriod(periodForPrint) : handlePrintAll}
          isGeneratingPdf={isGeneratingPdf}
        >
          {/* Single preview */}
          {pdfType === 'single' && periodForPrint && (
            <div
              id={`preview-financial-period-pdf-${periodForPrint.id}`}
              style={{
                width: '794px', height: '1123px', backgroundColor: '#ffffff', color: '#001437',
                padding: '24px', boxSizing: 'border-box',
                fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                direction: isRTL ? 'rtl' : 'ltr',
              }}
            >
              <div style={{ border: '2px solid #000', height: '100%', padding: '18px', position: 'relative', display: 'flex', flexDirection: 'column' }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundImage: `url(${headerLogo})`, backgroundSize: '95%', backgroundPosition: 'center center', backgroundRepeat: 'no-repeat', opacity: 0.08, zIndex: 0, pointerEvents: 'none' }} />
                <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <img src={`${publicUrl}/HeaderAce.png`} alt="Header" style={{ width: '100%', height: 'auto', objectFit: 'contain', marginBottom: '8px', borderBottom: '4px double #000', paddingBottom: '8px', flexShrink: 0 }} crossOrigin="anonymous" />
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '120px' }}>
                    <div style={{ marginTop: '12px', textAlign: 'center', marginBottom: '20px' }}>
                      <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#000080', margin: 0, direction: dir }}>{t('financialPeriods.print.title', { defaultValue: 'Financial Period Details' })}</h2>
                    </div>
                    <div style={{ border: '1px solid #000', padding: '12px', marginBottom: '12px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', fontSize: '13px', lineHeight: 1.6, direction: dir, textAlign }}>
                        <div><strong>{t('financialPeriods.print.name', { defaultValue: 'Period Name' })}:</strong> {periodForPrint.name || '—'}</div>
                        <div><strong>{t('financialPeriods.print.startDate', { defaultValue: 'Start Date' })}:</strong> {formatDate(periodForPrint.start_date)}</div>
                        <div><strong>{t('financialPeriods.print.endDate', { defaultValue: 'End Date' })}:</strong> {formatDate(periodForPrint.end_date)}</div>
                        <div><strong>{t('financialPeriods.print.status', { defaultValue: 'Status' })}:</strong> {periodForPrint.status || '—'}</div>
                        <div><strong>{t('financialPeriods.print.createdBy', { defaultValue: 'Created By' })}:</strong> {periodForPrint.created_by || '—'}</div>
                        <div><strong>{t('financialPeriods.print.createdAt', { defaultValue: 'Created At' })}:</strong> {formatDate(periodForPrint.created_at)}</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div style={{ position: 'absolute', bottom: '10px', left: '18px', right: '18px' }}>
                  <img src={`${publicUrl}/FooterAce.png`} alt="Footer" style={{ width: '100%', height: 'auto', objectFit: 'contain', borderTop: '4px double #000', paddingTop: '8px' }} crossOrigin="anonymous" />
                </div>
              </div>
            </div>
          )}

          {/* All-pages preview */}
          {pdfType === 'all' && (pdfChunks || []).map((chunk, chunkIndex) => (
            <FinancialPeriodsPdfPageTemplate
              key={chunkIndex}
              idPrefix="preview-financial-periods-pdf-page"
              periods={chunk.items}
              startIndex={chunk.startIndex}
              chunkIndex={chunkIndex}
              totalChunks={pdfChunks?.length || 1}
              isPreview={true}
              isRTL={isRTL}
              t={t}
              formatDate={formatDate}
              publicUrl={publicUrl}
              headerLogo={headerLogo}
              dir={dir}
              textAlign={textAlign}
            />
          ))}
        </PdfPreviewModal>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PDF Page Template — used for both hidden print and preview modal
// ─────────────────────────────────────────────────────────────────────────────
const FinancialPeriodsPdfPageTemplate = ({
  idPrefix, periods, startIndex = 0, chunkIndex = 0, totalChunks = 1,
  isPreview = true, isRTL, t, formatDate, publicUrl, headerLogo, dir, textAlign,
}) => {
  if (!periods || periods.length === 0) return null;

  return (
    <div
      id={`${idPrefix}-${chunkIndex}`}
      style={{
        position: isPreview ? 'relative' : 'absolute',
        left: isPreview ? 'auto' : '-9999px',
        top: isPreview ? 'auto' : 0,
        width: '794px', height: '1123px',
        backgroundColor: '#ffffff', color: '#001437',
        padding: '24px', boxSizing: 'border-box',
        fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
        direction: isRTL ? 'rtl' : 'ltr',
        marginBottom: isPreview && chunkIndex < totalChunks - 1 ? '20px' : 0,
      }}
    >
      <div style={{ border: '2px solid #000', height: '100%', padding: '18px', position: 'relative', display: 'flex', flexDirection: 'column' }}>
        {/* Watermark */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundImage: `url(${headerLogo})`, backgroundSize: '95%', backgroundPosition: 'center center', backgroundRepeat: 'no-repeat', opacity: 0.08, zIndex: 0, pointerEvents: 'none' }} />
        <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100%' }}>
          {/* Header image — sits outside flex:1 */}
          <img src={`${publicUrl}/HeaderAce.png`} alt="Header" style={{ width: '100%', height: 'auto', objectFit: 'contain', marginBottom: '8px', borderBottom: '4px double #000', paddingBottom: '8px', flexShrink: 0 }} crossOrigin="anonymous" />
          {/* Content — reserves 120px at bottom so footer never overlaps */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '120px' }}>
            <div style={{ marginTop: '12px', textAlign: 'center', marginBottom: '12px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#000080', margin: 0, direction: dir }}>
                {t('financialPeriods.pdf.reportTitle', { defaultValue: 'Financial Periods Report' })}
              </h2>
              <p style={{ fontSize: '12px', color: '#333', margin: '4px 0 0' }}>
                {new Date().toLocaleDateString(isRTL ? 'ar-SA' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>
            <div style={{ direction: isRTL ? 'rtl' : 'ltr', fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', border: '1px solid #000', backgroundColor: 'rgba(255,255,255,0.7)' }}>
                <thead>
                  <tr style={{ backgroundColor: 'rgba(238,242,247,0.7)' }}>
                    <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle', fontWeight: '700' }}>{t('common.serialNumber', { defaultValue: '#' })}</th>
                    <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle', fontWeight: '700' }}>{t('financialPeriods.table.name', { defaultValue: 'Period Name' })}</th>
                    <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle', fontWeight: '700' }}>{t('financialPeriods.table.startDate', { defaultValue: 'Start Date' })}</th>
                    <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle', fontWeight: '700' }}>{t('financialPeriods.table.endDate', { defaultValue: 'End Date' })}</th>
                    <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle', fontWeight: '700' }}>{t('financialPeriods.table.status', { defaultValue: 'Status' })}</th>
                    <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle', fontWeight: '700' }}>{t('financialPeriods.table.createdBy', { defaultValue: 'Created By' })}</th>
                    <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle', fontWeight: '700' }}>{t('financialPeriods.table.createdAt', { defaultValue: 'Created At' })}</th>
                  </tr>
                </thead>
                <tbody>
                  {periods.map((period, idx) => (
                    <tr key={period.id || idx} style={{ backgroundColor: idx % 2 === 0 ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.5)' }}>
                      <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>{startIndex + idx + 1}</td>
                      <td style={{ border: '1px solid #000', padding: '6px', direction: dir, textAlign, verticalAlign: 'middle' }}>{period.name || '—'}</td>
                      <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>{formatDate(period.start_date)}</td>
                      <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>{formatDate(period.end_date)}</td>
                      <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>{period.status || '—'}</td>
                      <td style={{ border: '1px solid #000', padding: '6px', direction: dir, textAlign, verticalAlign: 'middle' }}>{period.created_by || '—'}</td>
                      <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>{formatDate(period.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        {/* Footer image — absolutely positioned at bottom */}
        <div style={{ position: 'absolute', bottom: '10px', left: '18px', right: '18px', flexShrink: 0 }}>
          <img src={`${publicUrl}/FooterAce.png`} alt="Footer" style={{ width: '100%', height: 'auto', objectFit: 'contain', borderTop: '4px double #000', paddingTop: '8px' }} crossOrigin="anonymous" />
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Measurement Template — hidden, provides DOM heights to calculatePdfPagination
//
// topHeader = header image + title section (measured together as full top block)
// footer    = fixed 120px placeholder (mirrors paddingBottom:'120px' in page template)
// ─────────────────────────────────────────────────────────────────────────────
const FinancialPeriodsPdfMeasurementTemplate = ({ periods, isRTL, t, formatDate, publicUrl }) => {
  if (!periods || periods.length === 0) return null;

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute', left: '-9999px', top: 0,
        width: '794px', height: '1123px', backgroundColor: '#ffffff',
        padding: '24px', boxSizing: 'border-box',
        direction: isRTL ? 'rtl' : 'ltr',
      }}
    >
      <div style={{ border: '2px solid #000', height: '100%', padding: '18px', display: 'flex', flexDirection: 'column' }}>

        {/* topHeader: header image + report title — measured together */}
        <div id="financial-periods-pdf-measure-top-header" style={{ flexShrink: 0 }}>
          <img
            src={`${publicUrl}/HeaderAce.png`}
            alt=""
            style={{ width: '100%', height: 'auto', objectFit: 'contain', marginBottom: '8px', borderBottom: '4px double #000', paddingBottom: '8px' }}
            crossOrigin="anonymous"
          />
          <div style={{ marginTop: '12px', textAlign: 'center', marginBottom: '12px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#000080', margin: 0 }}>
              {t('financialPeriods.pdf.reportTitle', { defaultValue: 'Financial Periods Report' })}
            </h2>
            <p style={{ fontSize: '12px', color: '#333', margin: '4px 0 0' }}>
              {new Date().toLocaleDateString(isRTL ? 'ar-SA' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', border: '1px solid #000' }}>
          <thead id="financial-periods-pdf-measure-thead">
            <tr>
              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle', fontWeight: '700' }}>{t('common.serialNumber', { defaultValue: '#' })}</th>
              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle', fontWeight: '700' }}>{t('financialPeriods.table.name', { defaultValue: 'Period Name' })}</th>
              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle', fontWeight: '700' }}>{t('financialPeriods.table.startDate', { defaultValue: 'Start Date' })}</th>
              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle', fontWeight: '700' }}>{t('financialPeriods.table.endDate', { defaultValue: 'End Date' })}</th>
              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle', fontWeight: '700' }}>{t('financialPeriods.table.status', { defaultValue: 'Status' })}</th>
              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle', fontWeight: '700' }}>{t('financialPeriods.table.createdBy', { defaultValue: 'Created By' })}</th>
              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle', fontWeight: '700' }}>{t('financialPeriods.table.createdAt', { defaultValue: 'Created At' })}</th>
            </tr>
          </thead>
          <tbody id="financial-periods-pdf-measure-tbody">
            {periods.map((period, idx) => (
              <tr key={period.id || idx}>
                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>{idx + 1}</td>
                <td style={{ border: '1px solid #000', padding: '6px', verticalAlign: 'middle' }}>{period.name || '—'}</td>
                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>{formatDate(period.start_date)}</td>
                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>{formatDate(period.end_date)}</td>
                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>{period.status || '—'}</td>
                <td style={{ border: '1px solid #000', padding: '6px', verticalAlign: 'middle' }}>{period.created_by || '—'}</td>
                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>{formatDate(period.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Footer placeholder: 120px fixed height — mirrors paddingBottom:'120px' in page template */}
        <div id="financial-periods-pdf-measure-footer" style={{ height: '120px', flexShrink: 0 }} />
      </div>
    </div>
  );
};

export default FinancialPeriods;
